import { V4_DURATION_S, V4_KEYS, V4_POSTER_T, V4_POSTER_t } from './scenes';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** The prototype's piecewise --T -> --t map, for the fallback driver only. */
const tAt = (T: number): number => {
  for (let i = 1; i < V4_KEYS.length; i += 1) {
    if (T <= V4_KEYS[i][0]) {
      const [aT, at] = V4_KEYS[i - 1];
      const [bT, bt] = V4_KEYS[i];
      return at + (bt - at) * ((T - aT) / (bT - aT));
    }
  }
  return V4_KEYS[V4_KEYS.length - 1][1];
};

/*
 * V4 controller. The film itself is CSS: the hero root animates the registered
 * properties --v4T/--v4t, and every act reads them. This class only owns the
 * things CSS cannot — pausing out of view and on a hidden tab, the reduced
 * motion poster, the overlay-header hand-off, and a requestAnimationFrame
 * driver for engines without @property, which is the same fallback the approved
 * prototype ships. Kept from the previous V4 runtime so behaviour is unchanged.
 */
export class ShowcaseV4Engine {
  private static readonly VISIBLE_RATIO = 0.12;

  private readonly root: HTMLElement;
  private readonly visual: HTMLElement;
  private readonly header: HTMLElement | null;
  /** True when this engine can divide by a length, i.e. honour 100cqw / 1040px. */
  private readonly cqFit: boolean;
  /** True when the browser can animate the registered properties itself. */
  private readonly cssDriven: boolean;

  private elapsed = 0;
  private lastTs = 0;
  private raf = 0;
  private playing = false;
  private done = false;
  private inView = false;
  private engaged = false;
  private io?: IntersectionObserver;

  constructor(root: HTMLElement) {
    this.root = root;
    this.visual = root.querySelector<HTMLElement>('[data-v4-visual]')!;
    this.header = document.querySelector<HTMLElement>('header.site-header');
    this.cqFit =
      typeof CSS !== 'undefined' && CSS.supports('transform', 'scale(calc(100cqw / 1040px))');
    this.cssDriven =
      typeof CSS !== 'undefined' &&
      typeof CSS.registerProperty === 'function' &&
      getComputedStyle(root).animationName.includes('Launch');
  }

  private park(T: number, t = tAt(T)) {
    this.root.style.setProperty('--v4T', T.toFixed(3));
    this.root.style.setProperty('--v4t', t.toFixed(3));
  }

  private tick = (ts: number) => {
    if (!this.playing) {
      return;
    }
    // Clamp the delta so a throttled or backgrounded frame cannot jump the film.
    const dt = this.lastTs ? Math.min(ts - this.lastTs, 100) : 0;
    this.lastTs = ts;
    this.elapsed = (this.elapsed + dt / 1000) % V4_DURATION_S;
    this.park(this.elapsed);
    this.raf = requestAnimationFrame(this.tick);
  };

  private play() {
    if (this.done || this.playing) {
      return;
    }
    this.playing = true;
    if (this.cssDriven) {
      this.root.style.animationPlayState = 'running';
      return;
    }
    this.lastTs = 0;
    this.raf = requestAnimationFrame(this.tick);
  }

  private pause() {
    if (!this.playing) {
      return;
    }
    this.playing = false;
    if (this.cssDriven) {
      this.root.style.animationPlayState = 'paused';
      return;
    }
    cancelAnimationFrame(this.raf);
  }

  private sync = () => {
    if (this.inView && !document.hidden && !this.done) {
      this.play();
    } else {
      this.pause();
    }
  };

  /** Header chrome for the overlay compensation, plus the stage fit fallback. */
  private measure(): number {
    if (!this.cqFit) {
      const stage = this.visual.firstElementChild as HTMLElement | null;
      stage?.style.setProperty('--v4-s', (this.visual.clientWidth / 1040).toFixed(5));
    }

    const headerH = this.header ? this.header.offsetHeight : 0;
    const adminBar = parseFloat(getComputedStyle(document.documentElement).marginTop) || 0;
    const style = document.documentElement.style;
    style.setProperty('--hcc-header-h', `${headerH}px`);
    style.setProperty('--hcc-chrome-offset', `${adminBar + headerH}px`);
    return adminBar + headerH;
  }

  private onResize = () => {
    this.measure();
  };

  /* The takeover header is transparent only at the very top; any scroll hands
     the page to the site's standard white header until it returns to rest. */
  private onScroll = () => {
    const root = document.documentElement.dataset;
    if (window.scrollY > 4) {
      root.hccHeader = 'standard';
    } else {
      delete root.hccHeader;
    }
  };

  /** Play, or hold the prototype's settled poster frame under reduced motion. */
  private applyMotion = () => {
    if (window.matchMedia(REDUCED_MOTION).matches) {
      this.done = true;
      this.pause();
      delete this.root.dataset.v4Live;
      this.root.style.removeProperty('animation-play-state');
      this.park(V4_POSTER_T, V4_POSTER_t);
      this.measure();
    } else {
      this.done = false;
      this.root.dataset.v4Live = 'on';
      if (this.cssDriven) {
        this.root.style.removeProperty('--v4T');
        this.root.style.removeProperty('--v4t');
      } else {
        this.elapsed = 0;
        this.park(0);
      }
      this.sync();
    }
  };

  engage() {
    if (this.engaged) {
      return;
    }
    this.engaged = true;

    // 'on' arms the overlay header compensation, exactly as V2 and V3 do.
    document.documentElement.dataset.hccJs = 'on';
    this.root.dataset.hccBoot = 'ready';

    this.measure();

    this.io = new IntersectionObserver(
      (entries) => {
        this.inView = entries.some(
          (e) => e.isIntersecting && e.intersectionRatio >= ShowcaseV4Engine.VISIBLE_RATIO,
        );
        this.sync();
      },
      { threshold: [0, ShowcaseV4Engine.VISIBLE_RATIO, 0.6] },
    );
    this.io.observe(this.visual);

    // Two states for the takeover header: transparent over the gradient while
    // the page is at rest at the top, and the site's standard white header for
    // the rest of the page. A block set to the standard header has no overlay
    // to hand off, so it keeps the theme's own behaviour untouched.
    if (this.root.dataset.hccNav !== 'standard') {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      // Arms the header crossfade only after the first state is painted, so a
      // load at the top never animates into transparency.
      requestAnimationFrame(() => {
        document.documentElement.dataset.hccReady = '1';
      });
    }

    document.addEventListener('visibilitychange', this.sync);
    window.addEventListener('resize', this.onResize);

    this.applyMotion();
  }

  reducedMotionChange = () => {
    this.applyMotion();
  };
}
